import * as React from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Users,
  Briefcase,
  Wallet,
  Receipt,
  Lightbulb,
  Vault,
  Bell,
  BarChart3,
  UserCog,
  Building2,
  ClipboardList,
  Wallet2,
  LogOut,
  Tag,
  AlertCircle,
  FileSpreadsheet,
  Settings2,
  FileText,
  TrendingDown,
  FileDown,
  CalendarCheck,
  CalendarDays,
  CalendarClock,
  Sun,
  IndianRupee,
  ShieldCheck,
  Coins,
  Inbox,
  Settings,
  ChevronRight,
  Cog,
  Target,
  Clock,
  Menu,
  MessageSquare,
  UserRound,
  X,
} from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { canRead, type ReadArea, type RoleName } from '@bits/domain';
import { api } from '@/lib/api';
import { useChat } from '@/lib/chat-provider';
import { cn } from '@/lib/utils';
import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { WorkLogHeaderControl } from '@/components/WorkLogHeaderControl';
import { BrandFooter } from '@/components/BrandFooter';

type BadgeKey = 'my-tasks' | 'my-queue' | 'chat' | 'notifications';

type LeafItem = {
  kind: 'leaf';
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string | null;
  badge?: BadgeKey;
  area: ReadArea;
};

type ChildItem = {
  key: string;
  label: string;
  href: string | null;
  area: ReadArea;
};

type GroupItem = {
  kind: 'group';
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  children: ChildItem[];
};

type SidebarItem = LeafItem | GroupItem;

const SIDEBAR: SidebarItem[] = [
  {
    kind: 'leaf',
    key: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    href: '/',
    area: 'self',
  },
  {
    kind: 'leaf',
    key: 'my-tasks',
    label: 'My Tasks',
    icon: ClipboardList,
    href: '/my-tasks',
    badge: 'my-tasks',
    area: 'self',
  },
  {
    kind: 'leaf',
    key: 'my-queue',
    label: 'My Queue',
    icon: Inbox,
    href: '/my-queue',
    badge: 'my-queue',
    area: 'self',
  },
  {
    kind: 'leaf',
    key: 'chat',
    label: 'Chat',
    icon: MessageSquare,
    href: '/chat',
    badge: 'chat',
    area: 'self',
  },
  {
    kind: 'leaf',
    key: 'notifications',
    label: 'Notifications',
    icon: Bell,
    href: '/notifications',
    badge: 'notifications',
    area: 'self',
  },
  {
    kind: 'leaf',
    key: 'profile',
    label: 'My Profile',
    icon: UserRound,
    href: '/profile',
    area: 'self',
  },
  {
    kind: 'leaf',
    key: 'my-payslips',
    label: 'My Payslips',
    icon: FileText,
    href: '/my-payslips',
    area: 'self',
  },
  {
    kind: 'group',
    key: 'work-log',
    label: 'Work log',
    icon: Clock,
    children: [
      { key: 'work-log-clock', label: 'Clock in / out', href: '/work-log', area: 'self' },
      { key: 'work-log-history', label: 'History', href: '/work-log/history', area: 'self' },
    ],
  },
  {
    kind: 'group',
    key: 'crm',
    label: 'CRM',
    icon: Users,
    children: [
      { key: 'crm-clients', label: 'Clients', href: '/crm/clients', area: 'crm' },
      { key: 'crm-channels', label: 'Lead Channels', href: '/crm/channels', area: 'crm' },
    ],
  },
  {
    kind: 'group',
    key: 'sales',
    label: 'Sales',
    icon: Target,
    children: [
      { key: 'sales-prospects', label: 'Prospects', href: '/sales/prospects', area: 'sales' },
      { key: 'sales-pipeline', label: 'Pipeline', href: '/sales/pipeline', area: 'sales' },
      { key: 'sales-dashboard', label: 'Sales Dashboard', href: '/sales/dashboard', area: 'sales' },
      { key: 'sales-services', label: 'Services', href: '/sales/services', area: 'sales' },
    ],
  },
  {
    kind: 'group',
    key: 'projects',
    label: 'Projects',
    icon: Briefcase,
    children: [
      { key: 'projects-list', label: 'Projects', href: '/projects', area: 'projects' },
      { key: 'allocations', label: 'Allocations', href: '/allocations', area: 'projects' },
      { key: 'renewals', label: 'Renewals', href: '/renewals', area: 'renewals' },
    ],
  },
  {
    kind: 'group',
    key: 'ledger',
    label: 'Ledger',
    icon: Wallet,
    children: [
      {
        key: 'ledger-transactions',
        label: 'Transactions',
        href: '/ledger/transactions',
        area: 'ledger',
      },
      { key: 'ledger-accounts', label: 'Accounts', href: '/ledger/accounts', area: 'ledger' },
      { key: 'ledger-categories', label: 'Categories', href: '/ledger/categories', area: 'ledger' },
      { key: 'ledger-unmapped', label: 'Unmapped', href: '/ledger/unmapped', area: 'ledger' },
      { key: 'ledger-statements', label: 'Statements', href: '/ledger/statements', area: 'ledger' },
      {
        key: 'ledger-bank-templates',
        label: 'Bank Templates',
        href: '/ledger/bank-templates',
        area: 'ledger',
      },
    ],
  },
  {
    kind: 'group',
    key: 'billing',
    label: 'Billing',
    icon: Receipt,
    children: [
      { key: 'billing-documents', label: 'Documents', href: '/billing/documents', area: 'billing' },
      { key: 'billing-ar-aging', label: 'AR aging', href: '/billing/ar-aging', area: 'billing' },
      { key: 'billing-gstr1', label: 'GSTR-1 export', href: '/billing/gstr1', area: 'billing' },
    ],
  },
  {
    kind: 'group',
    key: 'people',
    label: 'People',
    icon: UserCog,
    children: [
      { key: 'people-attendance', label: 'Attendance', href: '/people/attendance', area: 'people' },
      { key: 'people-leave', label: 'Leave', href: '/people/leave', area: 'people' },
      {
        key: 'people-absences',
        label: 'Missed clock-ins',
        href: '/people/absences',
        area: 'people',
      },
      {
        key: 'people-holidays',
        label: 'Leave types & holidays',
        href: '/people/holidays',
        area: 'people',
      },
      { key: 'people-rates', label: 'Rates', href: '/people/rates', area: 'people_rates' },
      { key: 'people-kpi', label: 'Team KPIs', href: '/people/kpi', area: 'people_kpi' },
      {
        key: 'people-allocation-proposal',
        label: 'Allocation proposal',
        href: '/people/allocation-proposal',
        area: 'projects',
      },
    ],
  },
  {
    kind: 'group',
    key: 'payroll',
    label: 'Payroll',
    icon: IndianRupee,
    children: [
      { key: 'payroll-runs', label: 'Runs', href: '/payroll', area: 'payroll' },
      {
        key: 'statutory-config',
        label: 'Statutory config',
        href: '/statutory-config',
        area: 'payroll',
      },
      { key: 'period-close', label: 'Period Close', href: '/period-close', area: 'billing' },
    ],
  },
  {
    kind: 'leaf',
    key: 'ideas',
    label: 'Ideas & Initiatives',
    icon: Lightbulb,
    href: '/ideas',
    area: 'ideas',
  },
  {
    kind: 'leaf',
    key: 'vault',
    label: 'Company Vault',
    icon: Vault,
    href: '/vault',
    area: 'vault',
  },
  {
    kind: 'leaf',
    key: 'analytics',
    label: 'Analytics',
    icon: BarChart3,
    href: '/analytics',
    area: 'analytics',
  },
  {
    kind: 'group',
    key: 'setup',
    label: 'Setup',
    icon: Cog,
    children: [
      { key: 'company-profile', label: 'Company profile', href: '/company/profile', area: 'setup' },
      { key: 'cost-centers', label: 'Cost Centers', href: '/cost-centers', area: 'cost_centers' },
      {
        key: 'settings-notifications',
        label: 'Notification prefs',
        href: '/settings/notifications',
        area: 'self',
      },
      {
        key: 'settings-api-tokens',
        label: 'API tokens',
        href: '/settings/api-tokens',
        area: 'self',
      },
      {
        key: 'sales-csuite-members',
        label: 'C-Suite Members',
        href: '/sales/csuite-members',
        area: 'admin',
      },
      { key: 'people-access', label: 'People & Access', href: '/people-access', area: 'admin' },
    ],
  },
];

/**
 * Nav filtering is a convenience, not the control: the API enforces the same
 * areas server-side, so hiding a link never stands alone as the protection.
 */
function visibleSidebar(roles: readonly RoleName[]): SidebarItem[] {
  const out: SidebarItem[] = [];
  for (const item of SIDEBAR) {
    if (item.kind === 'leaf') {
      if (canRead(roles, item.area)) out.push(item);
      continue;
    }
    const children = item.children.filter((c) => canRead(roles, c.area));
    if (children.length > 0) out.push({ ...item, children });
  }
  return out;
}

function isPathActive(currentPath: string, href: string | null): boolean {
  if (!href) return false;
  return href === '/' ? currentPath === '/' : currentPath.startsWith(href);
}

function findActiveGroupKey(currentPath: string, items: SidebarItem[]): string | null {
  for (const item of items) {
    if (item.kind === 'group' && item.children.some((c) => isPathActive(currentPath, c.href))) {
      return item.key;
    }
  }
  return null;
}

export function AppShell({
  children,
  fullBleed = false,
}: {
  children: React.ReactNode;
  fullBleed?: boolean;
}): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const { unreadTotal: chatUnread } = useChat();

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => api.me(),
    retry: false,
  });

  const signedIn = meQuery.data?.person !== undefined;

  React.useEffect(() => {
    if (meQuery.isError && currentPath !== '/login') {
      void navigate({ to: '/login' });
    }
  }, [meQuery.isError, currentPath, navigate]);

  const myTasksQuery = useQuery({
    queryKey: ['my-tasks'],
    queryFn: () => api.getMyTasks(),
    enabled: signedIn,
    retry: false,
  });

  const queueQuery = useQuery({
    queryKey: ['queue-items'],
    queryFn: () => api.listQueueItems(),
    enabled: signedIn,
    retry: false,
  });

  const notificationsQuery = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.listNotifications(),
    enabled: signedIn,
    retry: false,
    refetchInterval: 60_000,
  });

  const overdueCount = myTasksQuery.data?.overdue.length ?? 0;
  const queueTotal = queueQuery.data?.total ?? 0;
  const unreadNotifications = notificationsQuery.data?.filter((n) => !n.read).length ?? 0;

  const logoutMutation = useMutation({
    mutationFn: () => api.logout(),
    onSuccess: async () => {
      queryClient.clear();
      await navigate({ to: '/login' });
    },
  });

  const displayName = meQuery.data?.person.name ?? '';

  const roles = React.useMemo<RoleName[]>(
    () => (meQuery.data?.person.roles ?? []) as RoleName[],
    [meQuery.data],
  );
  const sidebar = React.useMemo(() => visibleSidebar(roles), [roles]);

  const [openGroup, setOpenGroup] = React.useState<string | null>(() =>
    findActiveGroupKey(currentPath, SIDEBAR),
  );

  React.useEffect(() => {
    const active = findActiveGroupKey(currentPath, sidebar);
    if (active) setOpenGroup(active);
  }, [currentPath, sidebar]);

  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);

  React.useEffect(() => {
    setMobileNavOpen(false);
  }, [currentPath]);

  const renderBadge = (badge: BadgeKey | undefined): React.ReactNode => {
    if (badge === 'my-tasks' && overdueCount > 0) {
      return (
        <Badge variant="destructive" className="ml-auto">
          {overdueCount}
        </Badge>
      );
    }
    if (badge === 'my-queue' && queueTotal > 0) {
      return (
        <Badge variant="muted" className="ml-auto">
          {queueTotal}
        </Badge>
      );
    }
    if (badge === 'chat' && chatUnread > 0) {
      return (
        <Badge variant="default" className="ml-auto">
          {chatUnread}
        </Badge>
      );
    }
    if (badge === 'notifications' && unreadNotifications > 0) {
      return (
        <Badge variant="default" className="ml-auto">
          {unreadNotifications}
        </Badge>
      );
    }
    return null;
  };

  const rowBase = 'flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors';
  const childRowBase =
    'flex items-center gap-3 rounded-xl pl-11 pr-3 py-1.5 text-sm transition-colors';

  if (meQuery.isPending || meQuery.isError || !signedIn) {
    return <div className="min-h-screen bg-[hsl(var(--background))]" />;
  }

  const brand = (
    <div className="flex h-16 items-center gap-2.5 px-5">
      <img src="/logo-mark.png" alt="" className="size-8" />
      <span className="font-display text-xl font-semibold tracking-tight">IRIS</span>
    </div>
  );

  const nav = (
    <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
      {sidebar.map((item) => {
        const Icon = item.icon;

        if (item.kind === 'leaf') {
          const disabled = item.href === null;
          if (disabled) {
            return (
              <div
                key={item.key}
                aria-disabled="true"
                className={cn(rowBase, 'cursor-not-allowed text-[hsl(var(--muted-foreground))]/70')}
              >
                <Icon className="size-4" />
                <span>{item.label}</span>
                <span className="ml-auto text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]/60">
                  soon
                </span>
              </div>
            );
          }
          const active = isPathActive(currentPath, item.href);
          return (
            <Link
              key={item.key}
              to={item.href ?? undefined}
              className={cn(
                rowBase,
                active
                  ? 'bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] font-medium'
                  : 'text-[hsl(var(--foreground))]/80 hover:bg-[hsl(var(--muted))]',
              )}
            >
              <Icon className="size-4" />
              <span>{item.label}</span>
              {renderBadge(item.badge)}
            </Link>
          );
        }

        const isOpen = openGroup === item.key;
        const hasActiveChild = item.children.some((c) => isPathActive(currentPath, c.href));
        return (
          <div key={item.key}>
            <button
              type="button"
              onClick={() => setOpenGroup(isOpen ? null : item.key)}
              aria-expanded={isOpen}
              className={cn(
                rowBase,
                'w-full text-left',
                hasActiveChild
                  ? 'text-[hsl(var(--foreground))] font-medium'
                  : 'text-[hsl(var(--foreground))]/80 hover:bg-[hsl(var(--accent))]',
              )}
            >
              <Icon className="size-4" />
              <span>{item.label}</span>
              <ChevronRight
                className={cn('ml-auto size-4 transition-transform', isOpen && 'rotate-90')}
              />
            </button>
            {isOpen ? (
              <div className="mt-1 space-y-1">
                {item.children.map((c) => {
                  const disabled = c.href === null;
                  if (disabled) {
                    return (
                      <div
                        key={c.key}
                        aria-disabled="true"
                        className={cn(
                          childRowBase,
                          'cursor-not-allowed text-[hsl(var(--muted-foreground))]/70',
                        )}
                      >
                        <span>{c.label}</span>
                        <span className="ml-auto text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]/60">
                          soon
                        </span>
                      </div>
                    );
                  }
                  const active = isPathActive(currentPath, c.href);
                  return (
                    <Link
                      key={c.key}
                      to={c.href ?? undefined}
                      className={cn(
                        childRowBase,
                        active
                          ? 'bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] font-medium'
                          : 'text-[hsl(var(--foreground))]/80 hover:bg-[hsl(var(--muted))]',
                      )}
                    >
                      <span>{c.label}</span>
                    </Link>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </nav>
  );

  return (
    <div
      className={cn(
        'flex bg-[hsl(var(--background))]',
        fullBleed ? 'h-dvh overflow-hidden' : 'min-h-screen',
      )}
    >
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 border-r border-[hsl(var(--border))]/70 bg-[hsl(var(--card))] md:flex md:flex-col">
        {brand}
        {nav}
      </aside>

      <DialogPrimitive.Root open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-[hsl(var(--foreground))]/20 animate-[overlay-in_150ms_ease-out] md:hidden" />
          <DialogPrimitive.Content
            aria-describedby={undefined}
            className="fixed left-0 top-0 z-50 flex h-dvh w-72 max-w-[85vw] flex-col border-r border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-2xl shadow-[hsl(var(--foreground))]/10 animate-[drawer-in_280ms_cubic-bezier(0.32,0.72,0,1)] md:hidden"
          >
            <DialogPrimitive.Title className="sr-only">Menu</DialogPrimitive.Title>
            <div className="flex items-center justify-between pr-3">
              {brand}
              <DialogPrimitive.Close
                aria-label="Close menu"
                className="flex size-9 items-center justify-center rounded-full text-[hsl(var(--foreground))] transition-colors hover:bg-[hsl(var(--muted))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
              >
                <X className="size-5" />
              </DialogPrimitive.Close>
            </div>
            {nav}
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-2 border-b border-[hsl(var(--border))]/70 bg-[hsl(var(--background))]/80 px-3 backdrop-blur-xl supports-[backdrop-filter]:bg-[hsl(var(--background))]/65 sm:px-6">
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open menu"
              aria-expanded={mobileNavOpen}
              className="flex size-10 shrink-0 items-center justify-center rounded-xl text-[hsl(var(--foreground))] transition-colors hover:bg-[hsl(var(--muted))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] md:hidden"
            >
              <Menu className="size-5" />
            </button>
            {displayName ? (
              <Link
                to="/profile"
                className="hidden min-w-0 items-center gap-2.5 rounded-xl py-1 pl-1 pr-3 text-sm text-[hsl(var(--muted-foreground))] transition-colors hover:bg-[hsl(var(--muted))] sm:flex"
                aria-label="My profile"
              >
                <Avatar name={displayName} personId={meQuery.data?.person.id} size="sm" />
                <span className="truncate">
                  Signed in as{' '}
                  <span className="font-medium text-[hsl(var(--foreground))]">{displayName}</span>
                </span>
              </Link>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <WorkLogHeaderControl />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => logoutMutation.mutate()}
              disabled={logoutMutation.isPending}
              aria-label="Sign out"
            >
              <LogOut className="size-4" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </header>
        {fullBleed ? (
          <main className="flex min-h-0 flex-1 flex-col overflow-hidden">{children}</main>
        ) : (
          <main className="flex-1 overflow-y-auto">
            <div className="mx-auto flex max-w-5xl flex-col gap-8 px-4 py-6 sm:px-6 sm:py-10 md:px-8 md:py-14">
              {children}
            </div>
          </main>
        )}
        <BrandFooter className="border-t border-[hsl(var(--border))]/70 px-4 py-3 text-center sm:px-6 md:px-8" />
      </div>
    </div>
  );
}
